import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Globe, Music, Instagram, Facebook, Mail, CheckCircle, AlertCircle, ArrowLeft, Loader } from 'lucide-react';
import RnRLogo from '../components/public/RnRLogo';

const FIELDS = [
  { key: 'description', label: 'Beschreibung', type: 'textarea', placeholder: 'Beschreibt eure Band, euren Sound, eure Geschichte...' },
  { key: 'websiteUrl', label: 'Website', type: 'url', icon: Globe, placeholder: 'https://eure-band.de' },
  { key: 'playlistUrl', label: 'Musik-Link', type: 'url', icon: Music, placeholder: 'Spotify, Bandcamp, YouTube, SoundCloud...' },
  { key: 'instagramUrl', label: 'Instagram', type: 'url', icon: Instagram, placeholder: 'https://instagram.com/eureband' },
  { key: 'facebookUrl', label: 'Facebook', type: 'url', icon: Facebook, placeholder: 'https://facebook.com/eureband' },
  { key: 'contactInfo', label: 'Kontakt-Info', type: 'text', icon: Mail, placeholder: 'z.B. kontakt@eureband.de' },
];

export default function BandProfilePage() {
  const { token } = useParams();
  const [band, setBand] = useState(null);
  const [form, setForm] = useState({});
  const [status, setStatus] = useState('loading');
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`/api/band-profil/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.error || 'Ungültiger Link');
        }
        return r.json();
      })
      .then((data) => {
        setBand(data);
        setForm({
          description: data.description || '',
          websiteUrl: data.websiteUrl || '',
          playlistUrl: data.playlistUrl || '',
          instagramUrl: data.instagramUrl || '',
          facebookUrl: data.facebookUrl || '',
          contactInfo: data.contactInfo || '',
          contactEmail: data.contactEmail || '',
        });
        setStatus('idle');
      })
      .catch((err) => {
        setError(err.message);
        setStatus('error');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('saving');
    try {
      const res = await fetch(`/api/band-profil/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Fehler beim Speichern');
      }
      const data = await res.json();
      setEmailSent(data.emailSent);
      setStatus('success');
    } catch (err) {
      setError(err.message);
      setStatus('saveerror');
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="min-h-screen bg-bg-primary">
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">

        <div className="mb-10 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 font-body text-text-muted text-sm hover:text-accent-red transition-colors">
            <ArrowLeft size={16} /> Zur Website
          </Link>
          <RnRLogo className="h-14" />
        </div>

        {status === 'loading' && (
          <div className="flex items-center justify-center py-20 text-text-muted gap-3">
            <Loader size={20} className="animate-spin" />
            <span className="font-body text-sm">Laden...</span>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center py-20">
            <AlertCircle size={40} className="mx-auto mb-4 text-red-500" />
            <h2 className="font-heading text-2xl text-text-primary mb-3">Link ungültig</h2>
            <p className="font-body text-text-muted text-sm">{error}</p>
            <Link to="/" className="inline-block mt-6 text-accent-red font-body text-sm hover:underline">
              Zur Startseite
            </Link>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center py-16">
            <CheckCircle size={48} className="mx-auto mb-5 text-green-400" />
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-4">
              PROFIL GESPEICHERT
            </h2>
            <p className="font-body text-text-muted mb-3 leading-relaxed">
              Euer Profil für <strong className="text-text-primary">{band?.name}</strong> wurde erfolgreich aktualisiert.
            </p>
            {emailSent && (
              <div className="inline-flex items-center gap-2 bg-bg-secondary border border-divider rounded px-4 py-2.5 mt-2 mb-6">
                <Mail size={15} className="text-accent-red shrink-0" />
                <span className="font-body text-text-muted text-sm">
                  Wir haben euch einen Link zum Nachbearbeiten per E-Mail geschickt.
                </span>
              </div>
            )}
            {!emailSent && (
              <p className="font-body text-text-muted text-sm mt-2 mb-6">
                Speichert diesen Link — ihr könnt euer Profil jederzeit hier anpassen.
              </p>
            )}
            <button
              onClick={() => setStatus('idle')}
              className="font-body text-sm text-accent-red hover:underline"
            >
              Profil nochmals bearbeiten
            </button>
          </div>
        )}

        {(status === 'idle' || status === 'saving' || status === 'saveerror') && band && (
          <>
            <div className="mb-10">
              <p className="font-heading text-accent-red text-xs tracking-[4px] mb-2">BAND-PROFIL</p>
              <h1 className="font-heading text-4xl md:text-5xl text-text-primary leading-none mb-3">
                {band.name}
              </h1>
              <p className="font-body text-text-muted text-sm leading-relaxed">
                Füllt euer Profil aus — es erscheint auf der Rock'n'Rhein-Website, wenn Fans auf
                euren Namen klicken. Alle Felder sind optional.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {FIELDS.map(({ key, label, type, icon: Icon, placeholder }) => (
                <div key={key}>
                  <label className="block font-body text-text-primary text-sm font-medium mb-2">
                    {Icon && <span className="inline-flex items-center gap-1.5"><Icon size={13} className="opacity-60" /> {label}</span>}
                    {!Icon && label}
                  </label>
                  {type === 'textarea' ? (
                    <textarea
                      value={form[key] || ''}
                      onChange={set(key)}
                      rows={5}
                      placeholder={placeholder}
                      className="form-input w-full bg-bg-secondary border border-divider rounded px-4 py-3 font-body text-text-primary text-sm placeholder:text-text-muted/50 focus:outline-none focus:border-accent-red resize-y"
                    />
                  ) : (
                    <input
                      type={type}
                      value={form[key] || ''}
                      onChange={set(key)}
                      placeholder={placeholder}
                      className="form-input w-full bg-bg-secondary border border-divider rounded px-4 py-3 font-body text-text-primary text-sm placeholder:text-text-muted/50 focus:outline-none focus:border-accent-red"
                    />
                  )}
                </div>
              ))}

              <div className="pt-2 border-t border-divider">
                <label className="block font-body text-text-primary text-sm font-medium mb-1">
                  Eure E-Mail-Adresse
                </label>
                <p className="font-body text-text-muted text-xs mb-2">
                  Damit wir euch nach dem Speichern einen Link zum Nachbearbeiten schicken können.
                </p>
                <input
                  type="email"
                  value={form.contactEmail || ''}
                  onChange={set('contactEmail')}
                  placeholder="kontakt@eureband.de"
                  className="form-input w-full bg-bg-secondary border border-divider rounded px-4 py-3 font-body text-text-primary text-sm placeholder:text-text-muted/50 focus:outline-none focus:border-accent-red"
                />
              </div>

              {status === 'saveerror' && (
                <div className="flex items-center gap-2 text-red-400 font-body text-sm bg-red-950/30 border border-red-900/40 rounded px-4 py-3">
                  <AlertCircle size={15} />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'saving'}
                className="w-full bg-accent-red text-white font-heading text-xl py-4 rounded hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2 tracking-wide"
              >
                {status === 'saving' ? (
                  <><Loader size={18} className="animate-spin" /> SPEICHERN...</>
                ) : (
                  'PROFIL SPEICHERN'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
